import { Modal } from "@/components/elements/Modal";
import { FCC } from "@/types/common";
import { Box, CircularProgress, Stack, SxProps, Typography } from "@mui/material";
import { orange, text } from "@/theme/colors";
import {
  CASE_MATCH_TAG_DELETE_CONFIRM_MESSAGE,
  CASE_MATCH_TAG_DELETE_IN_USE_MESSAGE,
  CASE_MATCH_TAG_DELETE_NEWLY_ADDED_MESSAGE,
  CASE_MATCH_TAG_DELETE_SIMPLE_CONFIRM_MESSAGE,
  CASE_MATCH_TAG_DELETE_UNASSIGN_MESSAGE,
} from "@/features/case/constants/case-match-tag";
import { TDeleteTagOutcome, TTagEntityType, TTagUsingJob, validateDeleteTagApi } from "@/api/services/main/tag";
import { showError } from "@/utils/error";
import { UIEvent, useEffect, useRef, useState } from "react";

const PER_PAGE = 100;
const LIST_MAX_HEIGHT = 170;

const ITEM_LABEL_SX: SxProps = {
  color: text.primary,
  textAlign: "left",
  wordWrap: "break-word",
  whiteSpace: "normal",
  display: "inline-block",
  width: "90%",
};

const isNearBottom = (e: UIEvent<HTMLDivElement>) => {
  const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
  return scrollHeight - scrollTop <= clientHeight * 1.5;
};

type TItemProps = {
  item: TTagUsingJob;
};

const CaseMatchItem = ({ item }: TItemProps) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <Typography component="span" sx={{ color: text.primary, lineHeight: 1, flexShrink: 0 }}>
      ・
    </Typography>
    <Typography component="span" variant="body1" sx={ITEM_LABEL_SX}>
      {item.jobName}
    </Typography>
  </Box>
);

type TScrollListProps = {
  items: TTagUsingJob[];
  loading: boolean;
  onScroll: (e: UIEvent<HTMLDivElement>) => void;
};

const CaseMatchScrollList = ({ items, loading, onScroll }: TScrollListProps) => (
  <Stack spacing={1.5} onScroll={onScroll} sx={{ maxHeight: LIST_MAX_HEIGHT, overflow: "auto" }}>
    {items.map((item) => (
      <CaseMatchItem key={item.id} item={item} />
    ))}
    {loading && (
      <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
        <CircularProgress size={20} />
      </Box>
    )}
  </Stack>
);

type TConfirmDeleteCaseMatchTagModalProps = {
  open: boolean;
  onClose: () => void;
  onOk: (validatedAt: string) => Promise<TDeleteTagOutcome>;
  isLoading?: boolean;
  entityType: TTagEntityType;
  tagId: number;
  tagLabel: string;
  initialJobs: TTagUsingJob[];
  totalCount: number;
  initialValidatedAt: string;
};

export const ConfirmDeleteCaseMatchTagModal: FCC<TConfirmDeleteCaseMatchTagModalProps> = ({
  open,
  onClose,
  onOk,
  isLoading,
  entityType,
  tagId,
  tagLabel,
  initialJobs,
  totalCount,
  initialValidatedAt,
}) => {
  const [items, setItems] = useState<TTagUsingJob[]>(initialJobs);
  const [total, setTotal] = useState<number>(totalCount);
  const [reachedEnd, setReachedEnd] = useState<boolean>(initialJobs.length < PER_PAGE);
  const [loading, setLoading] = useState(false);
  const [cutoff, setCutoff] = useState(initialValidatedAt);

  const [newlyAdded, setNewlyAdded] = useState<TTagUsingJob[]>([]);
  const [newlySince, setNewlySince] = useState("");
  const [newlyCursor, setNewlyCursor] = useState<number | null>(null);
  const [newlyLoading, setNewlyLoading] = useState(false);

  const busyRef = useRef(false);

  useEffect(() => {
    setItems(initialJobs);
    setTotal(totalCount);
    setReachedEnd(initialJobs.length < PER_PAGE);
    setLoading(false);
    setCutoff(initialValidatedAt);
    setNewlyAdded([]);
    setNewlySince("");
    setNewlyCursor(null);
    setNewlyLoading(false);
    busyRef.current = false;
  }, [initialJobs, totalCount, tagId, initialValidatedAt]);

  const runExclusive = async (setSpinner: (v: boolean) => void, op: () => Promise<void>) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setSpinner(true);
    try {
      await op();
    } catch (e) {
      showError(e);
    } finally {
      busyRef.current = false;
      setSpinner(false);
    }
  };

  const loadMore = () => {
    if (reachedEnd) return;
    return runExclusive(setLoading, async () => {
      const res = await validateDeleteTagApi(entityType, tagId, items.at(-1)?.id, PER_PAGE, undefined, initialValidatedAt);
      const more = res.data?.jobs ?? [];
      if (more.length) setItems((prev) => [...prev, ...more]);
      if (res.data) setTotal(res.data.totalCount);
      setReachedEnd(more.length < PER_PAGE);
    });
  };

  const handleConfirm = () =>
    runExclusive(setLoading, async () => {
      const outcome = await onOk(cutoff);
      if (!outcome.success) {
        setNewlyAdded(outcome.jobs);
        setNewlySince(cutoff);
        setNewlyCursor(outcome.nextCursor);
        setCutoff(outcome.validatedAt);
      }
    });

  const loadMoreNewly = () => {
    if (newlyCursor == null) return;
    return runExclusive(setNewlyLoading, async () => {
      const res = await validateDeleteTagApi(entityType, tagId, newlyCursor, PER_PAGE, newlySince);
      const more = res.data?.jobs ?? [];
      if (more.length) setNewlyAdded((prev) => [...prev, ...more]);
      setNewlyCursor(res.data?.nextCursor ?? null);
    });
  };

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      onOk={handleConfirm}
      labelClose="キャンセル"
      labelOk="削除する"
      width={500}
      enableFooter
      isLoadingOkBtn={isLoading || loading}
    >
      <Box mb={2}>
        {total > 0 ? (
          <Box sx={{ mb: 2, mt: 1 }}>
            <Typography variant="body1">{CASE_MATCH_TAG_DELETE_IN_USE_MESSAGE(tagLabel, total)}</Typography>
            <Typography variant="body1">{CASE_MATCH_TAG_DELETE_UNASSIGN_MESSAGE}</Typography>
            <Typography variant="body1">{CASE_MATCH_TAG_DELETE_CONFIRM_MESSAGE}</Typography>
          </Box>
        ) : (
          <Typography variant="body1" sx={{ mt: 1 }}>
            {CASE_MATCH_TAG_DELETE_SIMPLE_CONFIRM_MESSAGE(tagLabel)}
          </Typography>
        )}
        {total > 0 && <CaseMatchScrollList items={items} loading={loading} onScroll={(e) => isNearBottom(e) && loadMore()} />}

        {newlyAdded.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="body1" sx={{ color: orange[80], mb: 1, whiteSpace: "pre-line" }}>
              {CASE_MATCH_TAG_DELETE_NEWLY_ADDED_MESSAGE}
            </Typography>
            <CaseMatchScrollList items={newlyAdded} loading={newlyLoading} onScroll={(e) => isNearBottom(e) && loadMoreNewly()} />
          </Box>
        )}
      </Box>
    </Modal>
  );
};
